import { useState } from "react";
import { MessageCircle, X } from "lucide-react";

/**
 * Floating "Chat on WhatsApp" widget for the storefront.
 * Enabled per-store via Settings -> Storefront -> WhatsApp chat button.
 * Uses the store's WhatsApp number (store_info.whatsapp) and opens wa.me.
 */
export default function StoreWhatsAppButton({ config }) {
  const [open, setOpen] = useState(false);
  const prefs = config?.preferences || {};
  const info = config?.store_info || {};
  const enabled = Boolean(prefs.whatsapp_chat_enabled);
  const rawNumber = (info.whatsapp || "").replace(/[^0-9]/g, "");
  if (!enabled || !rawNumber) return null;

  // Default to India country code if a 10-digit number was entered.
  const number = rawNumber.length === 10 ? `91${rawNumber}` : rawNumber;
  const storeName = info.name || "our store";
  const greeting = prefs.whatsapp_chat_message || `Hi! I have a question about ${storeName}.`;
  const href = `https://wa.me/${number}?text=${encodeURIComponent(greeting)}`;

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3" data-testid="store-whatsapp-widget">
      {open && (
        <div className="w-72 rounded-2xl bg-white shadow-2xl border border-stone-200 overflow-hidden animate-in fade-in slide-in-from-bottom-2">
          <div className="bg-[#25D366] px-4 py-3 flex items-center gap-2 text-white">
            <MessageCircle className="w-5 h-5" />
            <div className="font-semibold text-sm leading-tight flex-1">Chat with {storeName}</div>
            <button onClick={() => setOpen(false)} aria-label="Close" className="text-white/90 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-4">
            <div className="bg-stone-100 rounded-xl rounded-tl-sm p-3 text-sm text-stone-700">
              👋 Hi there! Have a question? Message us on WhatsApp and we&#39;ll reply as soon as we can.
            </div>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="store-whatsapp-start"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb959] text-white font-semibold rounded-xl py-2.5 text-sm transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> Start chat
            </a>
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat on WhatsApp"
        data-testid="store-whatsapp-fab"
        className="w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1fb959] text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-7 h-7" />}
      </button>
    </div>
  );
}
